// Make.com Webhook Integration
// Sends registration data after successful payment

export interface MakeWebhookData {
  type: string
  name: string
  email: string
  phone: string
  orderNumber: string
  ticketType: string
  price: string
  gender: string
  companionName?: string
  companionPhone?: string
  companionGender?: string
  nightChoice?: string
  roomType?: string
  roommate1?: string
  roommate2?: string
  competitiveTrack?: string
  rowingType?: string
  clubName?: string
  rentBoard?: string
  whatsappGroup?: string
  source: string
  paid: boolean

  // Meta Ads UTM Parameters
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string // Meta ad set/targeting info
  utm_id?: string // Meta campaign ID
  fbclid?: string
}

export async function sendToMake(data: MakeWebhookData): Promise<boolean> {
  const MAKE_WEBHOOK_URL =
    'https://hook.eu2.make.com/lrvn3a2u4kdjz84np141pmwu3d7hlrsb'

  try {
    console.log('🚀 Sending to Make.com:', data)

    const response = await fetch(MAKE_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Make.com webhook failed: ${response.status}`)
    }

    console.log('✅ Successfully sent to Make.com')
    return true
  } catch (error) {
    console.error('❌ Error sending to Make.com:', error)
    return false
  }
}

// ============================================
// Womens SUP Morning — dedicated Make.com webhook
// Triggered ONLY after a successful Tranzila payment.
// ============================================
export interface WomensSupRegistrationPayload {
  type: string
  source: string
  paid: boolean
  event: string
  eventDate: string
  selectedDate: string
  fullName: string
  email: string
  phone: string
  dateOfBirth: string
  noMedicalLimitations: boolean
  hasExperience: string
  notes: string
  price: number | string
  submittedAt: string

  // Meta Ads UTM Parameters
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  utm_id?: string
  fbclid?: string
}

// ============================================
// Wing Foil Campaign — dedicated Make.com webhook
// Triggered when a visitor submits the campaign lead form.
// No payment involved — this is a pure lead-capture flow.
// ============================================
export interface WingFoilLeadPayload {
  type: string
  source: string
  campaign: string
  fullName: string
  phone: string
  email: string
  submittedAt: string

  // Meta Ads UTM Parameters
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  utm_id?: string
  fbclid?: string
}

export async function sendWingFoilLeadToMake(
  data: WingFoilLeadPayload,
): Promise<boolean> {
  const WING_FOIL_WEBHOOK_URL =
    'https://hook.eu2.make.com/jy995dfnre5ip5rxv9j72j1pdu59toi2'

  try {
    console.log('🚀 Sending Wing Foil lead to Make.com:', data)

    const response = await fetch(WING_FOIL_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Make.com webhook failed: ${response.status}`)
    }

    console.log('✅ Successfully sent Wing Foil lead to Make.com')
    return true
  } catch (error) {
    console.error('❌ Error sending Wing Foil lead to Make.com:', error)
    return false
  }
}

// ============================================
// Corporate Team Building — dedicated Make.com webhook
// Triggered when a visitor submits the corporate lead form.
// Replace the placeholder below with the Custom Webhook URL created in Make.
// ============================================
export interface CorporateTeamBuildingLeadPayload {
  type: string
  source: string
  fullName: string
  company: string
  phone: string
  email: string
  groupSize: string
  preferredDate: string
  noDateYet: boolean
  activities: string[]
  notes: string
  submittedAt: string
  pageUrl: string
  referrer: string

  // Meta Ads UTM Parameters
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  utm_id?: string
  fbclid?: string
}

const CORPORATE_TEAM_BUILDING_WEBHOOK_URL =
  'https://hook.eu2.make.com/4yh4jhp6z68v8h4cw7n85qi3d5p3pt9i'

export async function sendCorporateTeamBuildingLeadToMake(
  data: CorporateTeamBuildingLeadPayload,
): Promise<boolean> {
  if (!CORPORATE_TEAM_BUILDING_WEBHOOK_URL.startsWith('https://hook.')) {
    console.error(
      '❌ Corporate Team Building Make webhook URL has not been configured.',
    )
    return false
  }

  try {
    const response = await fetch(CORPORATE_TEAM_BUILDING_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Make.com webhook failed: ${response.status}`)
    }

    console.log('✅ Successfully sent Corporate Team Building lead to Make.com')
    return true
  } catch (error) {
    console.error(
      '❌ Error sending Corporate Team Building lead to Make.com:',
      error,
    )
    return false
  }
}

export async function sendWomensSupRegistrationToMake(
  data: WomensSupRegistrationPayload,
): Promise<boolean> {
  const WOMENS_SUP_WEBHOOK_URL =
    'https://hook.eu2.make.com/299sca4jjebvxgen5zxryu60wb4m5gq0'

  try {
    console.log('🚀 Sending Womens SUP registration to Make.com:', data)

    const response = await fetch(WOMENS_SUP_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Make.com webhook failed: ${response.status}`)
    }

    console.log('✅ Successfully sent Womens SUP registration to Make.com')
    return true
  } catch (error) {
    console.error(
      '❌ Error sending Womens SUP registration to Make.com:',
      error,
    )
    return false
  }
}
